import { buildApiUrl, buildAssetUrl, withBusinessHeaders } from './apiConfig';

const SALES_ORDERS_API_URL =
  process.env.REACT_APP_SALES_ORDERS_API_URL || buildApiUrl('/sales-orders');

export async function fetchSalesOrders() {
  const response = await requestSalesOrderApi(SALES_ORDERS_API_URL, {
    fallbackMessage: 'Unable to load sales orders right now.',
  });

  return normalizeSalesOrdersResponse(await response.json());
}

export async function fetchSalesOrderFormOptions() {
  const response = await requestSalesOrderApi(`${SALES_ORDERS_API_URL}/form-options`, {
    fallbackMessage: 'Unable to load sales order form options right now.',
  });

  return response.json();
}

export async function createSalesOrder(payload) {
  const response = await requestSalesOrderApi(SALES_ORDERS_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to save sales order right now.',
  });

  return normalizeSalesOrderResponse(await response.json());
}

export async function updateSalesOrder(id, payload) {
  const response = await requestSalesOrderApi(`${SALES_ORDERS_API_URL}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update sales order right now.',
  });

  return normalizeSalesOrderResponse(await response.json());
}

export async function updateSalesOrderBilling(id, payload) {
  const response = await requestSalesOrderApi(`${SALES_ORDERS_API_URL}/${id}/billing`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update customer payment status right now.',
  });

  return normalizeSalesOrderResponse(await response.json());
}

export async function reopenSalesOrder(id) {
  const response = await requestSalesOrderApi(`${SALES_ORDERS_API_URL}/${id}/reopen`, {
    method: 'PATCH',
    fallbackMessage: 'Unable to reopen sales order right now.',
  });

  return normalizeSalesOrderResponse(await response.json());
}

export async function deleteSalesOrder(id) {
  const response = await requestSalesOrderApi(`${SALES_ORDERS_API_URL}/${id}`, {
    method: 'DELETE',
    fallbackMessage: 'Unable to cancel sales order right now.',
  });

  return normalizeSalesOrderResponse(await response.json());
}

function normalizeSalesOrdersResponse(salesOrders) {
  return Array.isArray(salesOrders) ? salesOrders.map(normalizeSalesOrderResponse) : [];
}

function normalizeSalesOrderResponse(salesOrder) {
  if (!salesOrder || typeof salesOrder !== 'object') {
    return salesOrder;
  }

  return {
    ...salesOrder,
    billingProofPreview: buildAssetUrl(salesOrder.billingProofPreview),
  };
}

async function requestSalesOrderApi(url, options) {
  const { fallbackMessage, ...requestOptions } = options;
  let response;

  try {
    response = await fetch(url, {
      ...requestOptions,
      headers: withBusinessHeaders(requestOptions.headers),
    });
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error, fallbackMessage));
  }

  if (!response.ok) {
    const errorPayload = await safeReadJson(response);
    throw new Error(errorPayload?.message || fallbackMessage);
  }

  return response;
}

function getNetworkErrorMessage(error, fallbackMessage) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You are offline right now. Please reconnect to continue.';
  }

  if (error instanceof TypeError) {
    return 'Unable to reach the server right now. Please check your connection or try again in a moment.';
  }

  return fallbackMessage;
}

async function safeReadJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}
